import type { JackpotData } from '@fanduel/casino-swj-consumer';

import type { RootState, SetState } from '../../useStore';

export type SwjJackpotData = JackpotData & {
  rules_page?: { url?: string };
  open_rules_externally_android?: boolean;
};

export type SwjSliceProps = {
  swj: {
    isTopBarVisible: boolean;
    setIsTopBarVisible: (isTopBarVisible: boolean) => void;
    jackpotData: SwjJackpotData | undefined;
    setJackpotData: (data: SwjJackpotData | undefined) => void;
    loading: boolean;
    setLoading: (loading: boolean) => void;
    error: boolean;
    setError: (error: boolean) => void;
    gameWindowExpansionState: boolean | undefined;
    setGameWindowExpansionState: (
      gameWindowExpansionState: boolean | undefined,
    ) => void;
    gameWindowExpansionInitialized: boolean;
    setGameWindowExpansionInitialized: (
      gameWindowExpansionInitialized: boolean,
    ) => void;
    gameExpansionToggleDisplayState: boolean;
    setGameExpansionToggleDisplayState: (
      gameExpansionToggleDisplayState: boolean,
    ) => void;
    gameWindowExpansionStateDisabled: boolean;
    setGameWindowExpansionStateDisabled: (
      gameWindowExpansionStateDisabled: boolean,
    ) => void;
    jackpotOptInStatus: number | undefined;
    setJackpotOptInStatus: (jackpotOptInStatus: number | undefined) => void;
    isDybFeatureFlagEnabled: boolean;
    setIsDybFeatureFlagEnabled: (isDybFeatureFlagEnabled: boolean) => void;
    isSwjFlagEnabled: boolean;
    setIsSwjFlagEnabled: (isSwjFlagEnabled: boolean) => void;
    isJackpotModeSuppressed: boolean;
    setIsJackpotModeSuppressed: (isJackpotModeSuppressed: boolean) => void;
  };
};

export const swjSlice = ({ set }: SetState<RootState>): SwjSliceProps => ({
  swj: {
    isTopBarVisible: true,
    setIsTopBarVisible: (isTopBarVisible: boolean) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            isTopBarVisible,
          },
        }),
        undefined,
        'swj/setIsTopBarVisible',
      ),
    jackpotData: undefined,
    setJackpotData: (data: SwjJackpotData | undefined) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            jackpotData: data,
            loading: false,
          },
        }),
        undefined,
        'swj/setJackpotData',
      ),
    loading: true,
    setLoading: (loading: boolean) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            loading,
          },
        }),
        undefined,
        'swj/setLoading',
      ),
    error: false,
    setError: (error: boolean) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            error,
          },
        }),
        undefined,
        'swj/setError',
      ),
    gameWindowExpansionState: false,
    setGameWindowExpansionState: (
      gameWindowExpansionState: boolean | undefined,
    ) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            gameWindowExpansionState,
            gameWindowExpansionInitialized: true,
          },
        }),
        undefined,
        'swj/setGameWindowExpansionState',
      ),
    gameWindowExpansionInitialized: false,
    setGameWindowExpansionInitialized: (
      gameWindowExpansionInitialized: boolean,
    ) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            gameWindowExpansionInitialized,
          },
        }),
        undefined,
        'swj/setGameWindowExpansionInitialized',
      ),
    gameExpansionToggleDisplayState: false,
    setGameExpansionToggleDisplayState: (
      gameExpansionToggleDisplayState: boolean,
    ) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            gameExpansionToggleDisplayState,
          },
        }),
        undefined,
        'swj/setGameExpansionToggleDisplayState',
      ),
    gameWindowExpansionStateDisabled: false,
    setGameWindowExpansionStateDisabled: (
      gameWindowExpansionStateDisabled: boolean,
    ) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            gameWindowExpansionStateDisabled,
          },
        }),
        undefined,
        'swj/setGameWindowExpansionStateDisabled',
      ),
    // undefined until the SWJ SDK reports opt-in; consumers needing a real value
    // should treat `undefined` as "not ready" (e.g. the DYB popup gate).
    jackpotOptInStatus: undefined,
    setJackpotOptInStatus: (jackpotOptInStatus: number | undefined) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            jackpotOptInStatus,
          },
        }),
        undefined,
        'swj/setJackpotOptInStatus',
      ),
    isDybFeatureFlagEnabled: false,
    setIsDybFeatureFlagEnabled: (isDybFeatureFlagEnabled: boolean) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            isDybFeatureFlagEnabled,
          },
        }),
        undefined,
        'swj/setIsDybFeatureFlagEnabled',
      ),
    isSwjFlagEnabled: false,
    setIsSwjFlagEnabled: (isSwjFlagEnabled: boolean) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            isSwjFlagEnabled,
          },
        }),
        undefined,
        'swj/setIsSwjFlagEnabled',
      ),
    isJackpotModeSuppressed: false,
    setIsJackpotModeSuppressed: (isJackpotModeSuppressed: boolean) =>
      set(
        (state: RootState) => ({
          ...state,
          swj: {
            ...state.swj,
            isJackpotModeSuppressed,
          },
        }),
        undefined,
        'swj/setIsJackpotModeSuppressed',
      ),
  },
});
